import { APP_EMITTER_EVENTS, AppEmitter, FormLabel, IValidateError, Textarea } from "shared";
import { ITemplateBarcodeComponent, ITemplateMultilineComponent } from "entities/template";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { IValidationFunc } from "../../../../lib/types/validation";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { useTranslation } from "react-i18next";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import styles from "./document_form_multiline.module.scss";

interface IDocumentFormInputProps {
    formRecord: Record<string, any>;
    component: ITemplateMultilineComponent | ITemplateBarcodeComponent;
    onChange: (tag: string, value?: string) => void;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    externalUpdateRecord: Record<string, any>;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
    triggerValidate: number;
    labelShort: boolean;
}

export const DocumentFormMultiline: React.FC<IDocumentFormInputProps> = React.memo(({
                                                                                        component,
                                                                                        formRecord,
                                                                                        readonly,
                                                                                        triggerValidate,
                                                                                        label,
                                                                                        labelShort,
                                                                                        externalUpdateRecord,
                                                                                        tooltip,
                                                                                        onValidationRegister,
                                                                                        onChange,
                                                                                    }) => {
    const t = useComponentsTranslations();
    const [value, setValue] = useState<string>("");
    const { i18n } = useTranslation();

    useEffect(() => {
        return () => {
            onValidationRegister(() => {
                return Promise.resolve(true)
            }, component.name);
            onChange(component.name, undefined);
        }
    }, [])

    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        }
    }, [])

    useEffect(() => {
        const updateVal = externalUpdateRecord[component.name];
        if (component.name) {
            setValue(updateVal ? updateVal : "");
        }
    }, [externalUpdateRecord, component.name])

    useEffect(() => {
        if (component.name) {
            onChange(component.name, value);
        }
    }, [value])

    const onChangeValue = (val: string) => {
        setValue(val);
    }

    useEffect(() => {
        onValidationRegister(validateInput, component.name)
    }, [component, formRecord])

    const handleClearEvent = () => {
        if (!readonly) {
            onChangeValue("");
        }
    }

    const required = useMemo(() => {
        return component.required !== false;
    }, [component]);

    const validateFunction = useCallback((value: string): IValidateError => {
        if (required && !value) {
            return {
                valid: false,
                message: t("requiredFieldError")
            }
        }

        return {
            valid: true
        }
    }, [component, i18n.language]);

    const validateInput = (): Promise<boolean> => {
        const valid = !required || validateFunction(value).valid;
        return Promise.resolve(valid);
    };

    return component.name ? <FormLabel title={label} required={required} disabled={readonly} labelShort={labelShort}>
        <div className={styles.container}>
            <Textarea name={component.name}
                      value={value}
                      disabled={readonly}
                      onChange={(e) => onChangeValue(e.target.value ? e.target.value : "")}
                      validate={required}
                      validateFunc={validateFunction}
                      triggerValidate={triggerValidate}
            />
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltip}/>
        </div>
    </FormLabel> : null;
});
